import { Component, inject, signal, OnInit, computed } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { TabsModule } from 'primeng/tabs';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { PaymentFlow } from '../../core/models';

// State → tag severity. The colour carries the meaning at a glance: an escalated flow is the only one
// asking for a person, and an orphaned one is money nobody has claimed yet.
const STATE_SEVERITY: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary'> = {
    Completed: 'success',
    Paid: 'info',
    AwaitingPayment: 'secondary',
    Abandoned: 'secondary',
    Reversed: 'warn',
    Escalated: 'danger'
};

// Compensation → severity. Failed is DANGER and must never read like None: one means "nothing to do",
// the other means we owe a customer money and could not send it.
const COMPENSATION_SEVERITY: Record<string, 'success' | 'info' | 'warn' | 'danger' | 'secondary'> = {
    None: 'secondary',
    Automatic: 'warn',
    Manual: 'info',
    Failed: 'danger'
};

@Component({
    selector: 'app-payment-flows',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, DialogModule, MessageModule, TabsModule, InputTextModule, TooltipModule, FormsModule, DecimalPipe, DatePipe],
    template: `
        <p-card>
            <ng-template pTemplate="title"><span>Payment flows<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Shows whether each payment ended up attached to an order." tooltipPosition="top"></i></span></ng-template>

            <p-tabs [(value)]="tab">
                <p-tablist>
                    <p-tab value="escalated">
                        Needs a decision<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Payments that need a person to decide what happens next. This list should be empty." tooltipPosition="top"></i>
                        @if (escalated().length) {
                            <p-tag severity="danger" [value]="String(escalated().length)" class="ml-2" />
                        }
                    </p-tab>
                    <p-tab value="compensations">Compensations<i class="pi pi-info-circle text-surface-400 ml-2 cursor-help" pTooltip="Money returned automatically, including attempts that failed." tooltipPosition="top"></i></p-tab>
                    <p-tab value="lookup">Find by cart</p-tab>
                </p-tablist>

                <p-tabpanels>
                    <!-- ============ THE OPERATOR QUEUE ============ -->
                    <p-tabpanel value="escalated">

                        @if (error()) { <p-message severity="error" [text]="error()!" styleClass="mb-3 w-full" /> }

                        <p-table [value]="escalated()" [loading]="loading()" dataKey="intentId" [rows]="25" [paginator]="escalated().length > 25">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Cart / order</th>
                                    <th>Amount</th>
                                    <th>Why it stopped</th>
                                    <th>Paid</th>
                                    <th></th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-f>
                                <tr>
                                    <td>
                                        <div class="font-medium">{{ f.clientReference }}</div>
                                        @if (f.clientOrderReference) {
                                            <div class="text-xs text-surface-500">order {{ f.clientOrderReference }}</div>
                                        }
                                    </td>
                                    <td class="tabular-nums">{{ f.amount | number: '1.2-2' }} {{ f.currency }}</td>
                                    <td class="max-w-md">
                                        <div class="text-sm">{{ f.escalationReason }}</div>
                                        @if (f.transientFailures > 0) {
                                            <div class="text-xs text-surface-500 mt-1">
                                                {{ f.transientFailures }} consecutive evaluation failure(s): {{ f.lastTransientError }}
                                            </div>
                                        }
                                    </td>
                                    <td class="text-sm">
                                        @if (f.paidAt) {
                                            {{ f.paidAt | date: 'short' }}
                                            <div class="text-xs text-surface-500">{{ f.isCaptured ? 'captured' : 'hold' }}</div>
                                        } @else { <span class="text-surface-500">—</span> }
                                    </td>
                                    <td><p-button label="Open" size="small" [text]="true" (onClick)="open(f)" /></td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="5" class="text-center py-6 text-surface-500">
                                    Nothing needs a decision.
                                </td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>

                    <!-- ============ COMPENSATIONS ============ -->
                    <p-tabpanel value="compensations">

                        <p-table [value]="compensated()" [loading]="loading()" dataKey="intentId" [rows]="25" [paginator]="compensated().length > 25">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Cart / order</th>
                                    <th>Amount</th>
                                    <th>How</th>
                                    <th>When</th>
                                    <th>Detail</th>
                                    <th></th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-f>
                                <tr>
                                    <td>
                                        <div class="font-medium">{{ f.clientReference }}</div>
                                        @if (f.clientOrderReference) {
                                            <div class="text-xs text-surface-500">order {{ f.clientOrderReference }}</div>
                                        }
                                    </td>
                                    <td class="tabular-nums">{{ f.amount | number: '1.2-2' }} {{ f.currency }}</td>
                                    <td><p-tag [severity]="compensationSeverity(f.compensation)" [value]="f.compensation" /></td>
                                    <td class="text-sm">{{ f.compensatedAt ? (f.compensatedAt | date: 'short') : '—' }}</td>
                                    <td class="text-sm max-w-md">{{ f.compensationDetail }}</td>
                                    <td><p-button label="Open" size="small" [text]="true" (onClick)="open(f)" /></td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="6" class="text-center py-6 text-surface-500">
                                    No compensations yet.
                                </td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>

                    <!-- ============ LOOKUP ============ -->
                    <p-tabpanel value="lookup">

                        <div class="flex flex-wrap items-end gap-2 mb-3">
                            <div>
                                <label class="block text-xs text-surface-500 mb-1">Location</label>
                                <input pInputText [(ngModel)]="locationId" placeholder="611718" class="w-40" />
                            </div>
                            <div>
                                <label class="block text-xs text-surface-500 mb-1">Cart / quote reference</label>
                                <input pInputText [(ngModel)]="clientReference" placeholder="600" class="w-52" (keyup.enter)="lookup()" />
                            </div>
                            <p-button label="Find" size="small" (onClick)="lookup()" [disabled]="!locationId || !clientReference" />
                        </div>

                        <p-table [value]="found()" [loading]="loading()" dataKey="intentId">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>State</th>
                                    <th>Amount</th>
                                    <th>Paid</th>
                                    <th>Order</th>
                                    <th>Opened</th>
                                    <th></th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-f>
                                <tr>
                                    <td>
                                        <p-tag [severity]="stateSeverity(f.state)" [value]="f.state" />
                                        @if (f.isOrphaned) {
                                            <p-tag severity="danger" value="orphaned" class="ml-1" />
                                        }
                                    </td>
                                    <td class="tabular-nums">{{ f.amount | number: '1.2-2' }} {{ f.currency }}</td>
                                    <td class="text-sm">{{ f.paidAt ? (f.paidAt | date: 'short') : '—' }}</td>
                                    <td class="text-sm">{{ f.clientOrderReference ?? '—' }}</td>
                                    <td class="text-sm">{{ f.createdAt | date: 'short' }}</td>
                                    <td><p-button label="Open" size="small" [text]="true" (onClick)="open(f)" /></td>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="emptymessage">
                                <tr><td colspan="6" class="text-center py-6 text-surface-500">
                                    No flows for that cart.
                                </td></tr>
                            </ng-template>
                        </p-table>
                    </p-tabpanel>
                </p-tabpanels>
            </p-tabs>
        </p-card>

        <!-- ============ THE FLOW LOG ============ -->
        <p-dialog [(visible)]="detailOpen" [modal]="true" [style]="{ width: '52rem' }" header="Payment flow">
            @if (selected(); as f) {
                <div class="flex flex-wrap items-center gap-2 mb-4">
                    <p-tag [severity]="stateSeverity(f.state)" [value]="f.state" />
                    @if (f.isOrphaned) { <p-tag severity="danger" value="orphaned" /> }
                    @if (f.compensation !== 'None') {
                        <p-tag [severity]="compensationSeverity(f.compensation)" [value]="'compensation: ' + f.compensation" />
                    }
                    <span class="text-sm text-surface-500">
                        {{ f.amount | number: '1.2-2' }} {{ f.currency }} · cart {{ f.clientReference }}
                        @if (f.clientOrderReference) { · order {{ f.clientOrderReference }} }
                    </span>
                </div>

                @if (f.escalationReason) {
                    <p-message severity="warn" [text]="f.escalationReason" styleClass="mb-4 w-full" />
                }
                @if (f.compensation === 'Failed') {
                    <p-message severity="error" styleClass="mb-4 w-full"
                               text="This money could not be returned automatically. It has NOT been refunded." />
                }

                <div class="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5 text-sm">
                    <div>
                        <div class="text-xs text-surface-500">Paid</div>
                        <div>{{ f.paidAt ? (f.paidAt | date: 'short') : '—' }}</div>
                        @if (f.paidAt) { <div class="text-xs text-surface-500">{{ f.isCaptured ? 'captured' : 'hold' }}</div> }
                    </div>
                    <div>
                        <div class="text-xs text-surface-500">Order acknowledged</div>
                        <div>{{ f.orderCommittedAt ? (f.orderCommittedAt | date: 'short') : '—' }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-surface-500">Checks made</div>
                        <div class="tabular-nums">{{ f.rung }}</div>
                    </div>
                    <div>
                        <div class="text-xs text-surface-500">Next check</div>
                        <div>{{ f.nextCheckAt ? (f.nextCheckAt | date: 'short') : 'none' }}</div>
                    </div>
                </div>

                <h4 class="font-medium mb-2">Activity log</h4>
                <p-table [value]="f.transitions" dataKey="at" size="small">
                    <ng-template pTemplate="header">
                        <tr><th>When</th><th>Move</th><th>Reason</th><th>Detail</th></tr>
                    </ng-template>
                    <ng-template pTemplate="body" let-t>
                        <tr>
                            <td class="text-xs whitespace-nowrap">{{ t.at | date: 'short' }}</td>
                            <td class="text-xs whitespace-nowrap">
                                @if (t.from && t.from !== t.to) {
                                    {{ t.from }} → <strong>{{ t.to }}</strong>
                                } @else {
                                    <span class="text-surface-500">{{ t.to }}</span>
                                }
                            </td>
                            <td class="text-xs"><code>{{ t.reason }}</code></td>
                            <td class="text-xs">{{ t.detail }}</td>
                        </tr>
                    </ng-template>
                </p-table>
            }
        </p-dialog>
    `
})
export class PaymentFlows implements OnInit {
    private api = inject(MiddlewareApi);
    private route = inject(ActivatedRoute);

    protected readonly String = String;

    tab = 'escalated';
    locationId = '';
    clientReference = '';

    escalated = signal<PaymentFlow[]>([]);
    compensated = signal<PaymentFlow[]>([]);
    found = signal<PaymentFlow[]>([]);
    selected = signal<PaymentFlow | null>(null);
    loading = signal(false);
    error = signal<string | null>(null);
    detailOpen = false;

    ngOnInit(): void {
        this.load();

        // Deep link from a payment: /payment-flows?intentId=…
        const intentId = this.route.snapshot.queryParamMap.get('intentId');
        if (intentId) this.openById(intentId);
    }

    load(): void {
        this.loading.set(true);
        this.error.set(null);

        this.api.getEscalatedPaymentFlows().subscribe({
            next: (f) => { this.escalated.set(f); this.loading.set(false); },
            error: (e) => { this.error.set(this.message(e)); this.loading.set(false); }
        });

        this.api.getCompensatedPaymentFlows().subscribe({
            next: (f) => this.compensated.set(f),
            error: () => { /* the escalated list already surfaced the failure */ }
        });
    }

    lookup(): void {
        if (!this.locationId || !this.clientReference) return;
        this.loading.set(true);
        this.error.set(null);

        this.api.getPaymentFlowsByReference(this.locationId, this.clientReference).subscribe({
            next: (f) => { this.found.set(f); this.loading.set(false); },
            error: (e) => { this.error.set(this.message(e)); this.loading.set(false); }
        });
    }

    open(flow: PaymentFlow): void {
        // Re-read rather than trusting the row: a list can be minutes old, and the whole value of the log is
        // that it is the current account of what happened.
        this.openById(flow.intentId);
    }

    private openById(intentId: string): void {
        this.api.getPaymentFlow(intentId).subscribe({
            next: (f) => { this.selected.set(f); this.detailOpen = true; },
            error: (e) => this.error.set(this.message(e))
        });
    }

    stateSeverity(state: string) { return STATE_SEVERITY[state] ?? 'secondary'; }
    compensationSeverity(mode: string) { return COMPENSATION_SEVERITY[mode] ?? 'secondary'; }

    private message(e: any): string {
        return e?.error?.message ?? e?.message ?? 'Could not load payment flows.';
    }
}
